import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminPartnersService } from "@/services/admin-partners-service";
import {
  PartnerAiState,
  PartnerAttractionsState,
  PartnerCuratedList,
  PartnerDestinationAiRegenerateRequest,
  PartnerDestinationAiStatus,
  PartnerDestinationAttractions,
  PartnerDestinationDetail,
  PartnerDestinationProduct,
  PartnerDestinationProductsQuery,
  PartnerDestinationProductsResponse,
  PartnerDestinationSummary,
  PartnerDestinationSyncStatus,
  UpdatePartnerCuratedListRequest,
} from "@/types/partner-destination";
import { isTerminalPartnerSyncState } from "@/types/partner-settings";
import { getErrorMessage, getHttpStatus } from "@/utils/httpErrors";
import { describeError } from "../utils/describeError";

const DESTINATION_SYNC_POLL_INTERVAL_MS = 5000;
const DESTINATION_SYNC_POLL_TIMEOUT_MS = 10 * 60 * 1000;

// The backend caps `limit` at 200 (400 "limit must not be greater than
// 200" — sim, 2026-09-28). The curated list itself has no such cap, so
// `useAllCuratedProducts` below pages through in chunks of this size.
const CURATED_PRODUCTS_PAGE_LIMIT = 200;

export const destinationKeys = {
  all: ["partnerDestinations"] as const,
  lists: () => [...destinationKeys.all, "list"] as const,
  detail: (destinationId: string) =>
    [...destinationKeys.all, "detail", destinationId] as const,
  products: (destinationId: string, query: PartnerDestinationProductsQuery) =>
    [...destinationKeys.all, "products", destinationId, query] as const,
  curated: (destinationId: string) =>
    [...destinationKeys.all, "curated", destinationId] as const,
  curatedProducts: (destinationId: string) =>
    [...destinationKeys.all, "curatedProducts", destinationId] as const,
  syncStatus: (destinationId: string) =>
    [...destinationKeys.all, "syncStatus", destinationId] as const,
  aiStatus: (destinationId: string) =>
    [...destinationKeys.all, "aiStatus", destinationId] as const,
  attractions: (destinationId: string) =>
    [...destinationKeys.all, "attractions", destinationId] as const,
};

export const useDestinations = () => {
  return useQuery<PartnerDestinationSummary[]>({
    queryKey: destinationKeys.lists(),
    queryFn: () => adminPartnersService.listDestinations(),
    staleTime: 60 * 1000,
  });
};

export const useDestination = (destinationId: string | undefined) => {
  return useQuery<PartnerDestinationDetail>({
    queryKey: destinationKeys.detail(destinationId || ""),
    enabled: !!destinationId,
    queryFn: () => adminPartnersService.getDestination(destinationId!),
    staleTime: 30 * 1000,
  });
};

export const useDestinationProducts = (
  destinationId: string | undefined,
  query: PartnerDestinationProductsQuery,
  options?: { refetchInterval?: number | false },
) => {
  return useQuery<PartnerDestinationProductsResponse>({
    queryKey: destinationKeys.products(destinationId || "", query),
    enabled: !!destinationId,
    queryFn: () =>
      adminPartnersService.getDestinationProducts(destinationId!, query),
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000,
    // While a sync is filling an empty cache, poll faster so the catalog
    // panel's "N loaded" count moves (owner report, 2026-09-28: the cache
    // is empty until the first Sync and nothing told them to press it).
    refetchInterval: options?.refetchInterval ?? false,
  });
};

export const useCuratedList = (destinationId: string | undefined) => {
  return useQuery<PartnerCuratedList>({
    queryKey: destinationKeys.curated(destinationId || ""),
    enabled: !!destinationId,
    queryFn: () => adminPartnersService.getCuratedList(destinationId!),
    staleTime: 30 * 1000,
  });
};

/**
 * The curated list's full product detail (for the right-hand panel),
 * enriched via `products?curated=true`. That endpoint is paged like any
 * other — `limit` capped at 200 server-side — so a curated list longer
 * than one page is walked to completion here rather than requested in one
 * over-sized call (the bug sim hit, 2026-09-28: `limit: 1000` → 400).
 */
export const useAllCuratedProducts = (destinationId: string | undefined) => {
  return useQuery<PartnerDestinationProduct[]>({
    queryKey: destinationKeys.curatedProducts(destinationId || ""),
    enabled: !!destinationId,
    queryFn: async () => {
      const first = await adminPartnersService.getDestinationProducts(
        destinationId!,
        { page: 1, limit: CURATED_PRODUCTS_PAGE_LIMIT, curated: true },
      );
      const items = [...first.data];
      const totalPages = Math.ceil(first.total / CURATED_PRODUCTS_PAGE_LIMIT);
      for (let page = 2; page <= totalPages; page += 1) {
        const next = await adminPartnersService.getDestinationProducts(
          destinationId!,
          { page, limit: CURATED_PRODUCTS_PAGE_LIMIT, curated: true },
        );
        items.push(...next.data);
      }
      return items;
    },
    staleTime: 30 * 1000,
  });
};

export type CuratedSaveBlockReason =
  | "notLoaded"
  | "loadError"
  | "emptyEnabled"
  | null;

/**
 * Centralizes why Save / Save & apply / the enabled toggle are disabled, so
 * a disabled control always has a reason rendered next to it. Sim,
 * 2026-09-28: the curated products fetch failed, the panel was blank, the
 * button was disabled, and clicking a disabled button fires no handler at
 * all — 20s of total silence. A block must always say why.
 *
 * `isLoaded` gates on BOTH `GET .../curated` (positions, the enabled flag)
 * and the curated-products fetch (the same positions enriched with title/
 * image/etc. for the cards) — the editable `items` list is seeded from the
 * latter, so it isn't trustworthy until both have succeeded. This is scoped
 * to the curated (right) panel only: the catalog (left) panel's own load
 * failure must never block or blank the curated side (sim, 2026-09-28 —
 * "Save & apply" sent `items: []` over 5 real positions because the two
 * were wrongly coupled).
 */
export function getCuratedSaveBlockReason(params: {
  isLoaded: boolean;
  isLoadError: boolean;
  enabled: boolean;
  itemsCount: number;
}): CuratedSaveBlockReason {
  if (params.isLoadError) return "loadError";
  if (!params.isLoaded) return "notLoaded";
  if (params.enabled && params.itemsCount === 0) return "emptyEnabled";
  return null;
}

/**
 * True when a save would wipe a previously non-empty curated list — the
 * caller must get an explicit confirmation before submitting (sim,
 * 2026-09-28: an empty `items: []` silently overwrote 5 saved positions;
 * restored by hand via the API).
 */
export function needsClearAllConfirmation(
  itemsCount: number,
  seededItemsCount: number,
): boolean {
  return itemsCount === 0 && seededItemsCount > 0;
}

/**
 * Shared polling loop behind both entry points that queue a destination
 * sync job: the "Sync" button (`POST .../sync`) and "Save & apply to
 * hotels" (`PUT .../curated`, which also answers 202 + `syncId`). Mirrors
 * `usePartnerSync` (`partner-settings.hooks.ts`), scoped to a destination
 * instead of a hotel.
 */
interface SyncPollMessages {
  succeeded: string;
  partial: string;
  failed: string;
}

const useDestinationSyncPoll = (
  destinationId: string | undefined,
  onSyncCompleted?: () => void,
  resumeSyncId?: string | null,
  messages?: SyncPollMessages,
) => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [syncId, setSyncId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const pollStartRef = useRef<number | null>(null);
  const timedOutRef = useRef(false);

  const invalidateAfterSync = useCallback(() => {
    if (!destinationId) return;
    qc.invalidateQueries({ queryKey: destinationKeys.lists() });
    qc.invalidateQueries({ queryKey: destinationKeys.detail(destinationId) });
    qc.invalidateQueries({
      queryKey: [...destinationKeys.all, "products", destinationId],
    });
    qc.invalidateQueries({ queryKey: destinationKeys.curated(destinationId) });
    qc.invalidateQueries({
      queryKey: destinationKeys.curatedProducts(destinationId),
    });
  }, [destinationId, qc]);

  const statusQuery = useQuery<PartnerDestinationSyncStatus>({
    queryKey: destinationKeys.syncStatus(destinationId || ""),
    enabled: !!destinationId && !!syncId,
    queryFn: () =>
      adminPartnersService.getDestinationSyncStatus(destinationId!),
    refetchInterval: (query) => {
      if (timedOutRef.current) return false;
      if (isTerminalPartnerSyncState(query.state.data?.state)) return false;
      if (
        pollStartRef.current !== null &&
        Date.now() - pollStartRef.current > DESTINATION_SYNC_POLL_TIMEOUT_MS
      ) {
        timedOutRef.current = true;
        return false;
      }
      return DESTINATION_SYNC_POLL_INTERVAL_MS;
    },
  });

  useEffect(() => {
    if (!syncId) return;

    if (timedOutRef.current) {
      setIsSyncing(false);
      setSyncId(null);
      pollStartRef.current = null;
      timedOutRef.current = false;
      toast.error(t("partners.syncResult.timeout"));
      return;
    }

    const status = statusQuery.data;
    if (!status || !isTerminalPartnerSyncState(status.state)) return;

    setIsSyncing(false);
    setSyncId(null);
    pollStartRef.current = null;
    invalidateAfterSync();

    const resolved: SyncPollMessages = messages ?? {
      succeeded: t("partners.admin.destinations.syncResult.succeeded"),
      partial: t("partners.admin.destinations.syncResult.partial"),
      failed: t("partners.admin.destinations.syncResult.failed"),
    };

    if (status.state === "succeeded") {
      toast.success(resolved.succeeded);
      onSyncCompleted?.();
    } else if (status.state === "partial") {
      toast.warning(resolved.partial);
      onSyncCompleted?.();
    } else {
      toast.error(status.lastError || resolved.failed);
    }
    // `messages` intentionally left out: it's a literal built fresh each
    // render at the call site, so tracking it by reference would refire
    // this effect every render for no reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusQuery.data, syncId, invalidateAfterSync, onSyncCompleted, t]);

  const startPolling = useCallback((nextSyncId: string) => {
    timedOutRef.current = false;
    pollStartRef.current = Date.now();
    setIsSyncing(true);
    setSyncId(nextSyncId);
  }, []);

  // The destination card reports a job already `queued`/`running` (e.g. the
  // page was reloaded mid-sync) — pick the poll back up instead of leaving
  // a stale badge with no updates until the next manual action.
  const hasResumedRef = useRef(false);
  useEffect(() => {
    if (hasResumedRef.current || syncId || !resumeSyncId) return;
    hasResumedRef.current = true;
    startPolling(resumeSyncId);
  }, [resumeSyncId, syncId, startPolling]);

  return { isSyncing, startPolling, liveStatus: statusQuery.data ?? null };
};

export const useDestinationSync = (
  destinationId: string | undefined,
  options?: { onSyncCompleted?: () => void; resumeSyncId?: string | null },
) => {
  const { t } = useTranslation();
  const { isSyncing, startPolling, liveStatus } = useDestinationSyncPoll(
    destinationId,
    options?.onSyncCompleted,
    options?.resumeSyncId,
    // Undefined — the poll falls back to the catalog-sync wording, which is
    // correct for the "Sync" button.
    undefined,
  );

  const triggerMutation = useMutation({
    mutationFn: async () => {
      if (!destinationId) throw new Error("destinationId is required");
      return adminPartnersService.syncDestination(destinationId);
    },
    onSuccess: (response) => {
      startPolling(response.syncId);
      toast.success(t("partners.syncStarted"));
    },
    onError: (error) => {
      toast.error(describeError(t("partners.syncErrorMessage"), error));
    },
  });

  return {
    isSyncing: isSyncing || triggerMutation.isPending,
    triggerSync: () => triggerMutation.mutate(),
    liveStatus,
  };
};

export const useUpdateCuratedList = (
  destinationId: string | undefined,
  options?: { onSyncCompleted?: () => void },
) => {
  const { t } = useTranslation();
  const { isSyncing, startPolling, liveStatus } = useDestinationSyncPoll(
    destinationId,
    options?.onSyncCompleted,
    undefined,
    {
      succeeded: t("partners.admin.destinations.applyResult.succeeded"),
      partial: t("partners.admin.destinations.applyResult.partial"),
      failed: t("partners.admin.destinations.applyResult.failed"),
    },
  );
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: UpdatePartnerCuratedListRequest) => {
      if (!destinationId) throw new Error("destinationId is required");
      return adminPartnersService.updateCuratedList(destinationId, data);
    },
    onSuccess: (response) => {
      if (!destinationId) return;
      qc.invalidateQueries({
        queryKey: destinationKeys.curated(destinationId),
      });
      startPolling(response.syncId);
    },
    onError: (error) => {
      // Task 0032, contract 2026-09-29: adding an attraction-kind cache item
      // to the curated list now answers 400 "Not bookable" — a fixed
      // translated message, never the raw body (this feature's hard rule).
      if (
        getHttpStatus(error) === 400 &&
        getErrorMessage(error)?.toLowerCase().includes("not bookable")
      ) {
        toast.error(t("partners.admin.destinations.detail.notBookableError"));
        return;
      }
      toast.error(
        describeError(t("partners.admin.destinations.detail.saveError"), error),
      );
    },
  });

  return {
    saveCuratedList: mutation.mutateAsync,
    isSaving: mutation.isPending,
    isApplying: isSyncing,
    liveStatus,
  };
};

// Task 0027 — AI auto-curation. Contract confirmed 2026-09-28 against
// backend dev@b2d5a1e0 (see partner-destination.ts).
const AI_STATUS_POLL_INTERVAL_MS = 5000;

/**
 * Status + top-20 for the "AI curation" card. Self-polls while queued/running
 * (no separate "is this syncing" flag needed, unlike the catalog-sync poll —
 * the status response's own `aiState` drives `refetchInterval`), and
 * invalidates the products/curated/list queries once a run lands on
 * succeeded/failed so the card, the catalog and the Cities table badge pick
 * up the new scores and positions without a manual refresh.
 */
export const useAiStatus = (destinationId: string | undefined) => {
  const qc = useQueryClient();
  const prevStateRef = useRef<PartnerAiState | undefined>(undefined);

  const query = useQuery<PartnerDestinationAiStatus>({
    queryKey: destinationKeys.aiStatus(destinationId || ""),
    enabled: !!destinationId,
    queryFn: () => adminPartnersService.getAiStatus(destinationId!),
    staleTime: 15 * 1000,
    refetchInterval: (q) => {
      const state = q.state.data?.aiState;
      return state === "queued" || state === "running"
        ? AI_STATUS_POLL_INTERVAL_MS
        : false;
    },
  });

  useEffect(() => {
    if (!destinationId) return;
    const prev = prevStateRef.current;
    const next = query.data?.aiState;
    if (
      next &&
      prev &&
      prev !== next &&
      (next === "succeeded" || next === "failed")
    ) {
      qc.invalidateQueries({ queryKey: destinationKeys.lists() });
      qc.invalidateQueries({ queryKey: destinationKeys.detail(destinationId) });
      qc.invalidateQueries({
        queryKey: [...destinationKeys.all, "products", destinationId],
      });
      qc.invalidateQueries({
        queryKey: destinationKeys.curated(destinationId),
      });
      qc.invalidateQueries({
        queryKey: destinationKeys.curatedProducts(destinationId),
      });
    }
    prevStateRef.current = next;
  }, [query.data?.aiState, destinationId, qc]);

  return query;
};

export const useRegenerateAi = (destinationId: string | undefined) => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: PartnerDestinationAiRegenerateRequest) => {
      if (!destinationId) throw new Error("destinationId is required");
      return adminPartnersService.regenerateAi(destinationId, data);
    },
    onSuccess: () => {
      if (!destinationId) return;
      qc.invalidateQueries({
        queryKey: destinationKeys.aiStatus(destinationId),
      });
      toast.success(t("partners.admin.destinations.ai.regenerateStarted"));
    },
    onError: (error) => {
      toast.error(
        describeError(
          t("partners.admin.destinations.ai.regenerateError"),
          error,
        ),
      );
    },
  });

  return {
    // `keepManual` defaults true (backend default) unless the caller opts out.
    regenerate: (options?: { keepManual?: boolean }) =>
      mutation.mutate({ keepManual: options?.keepManual ?? true }),
    isRegenerating: mutation.isPending,
  };
};

// SUPER_ADMIN only, from the Cities list page. Queues AI runs for every
// destination without one yet; each destination's own `useAiStatus` picks
// up the resulting queued/running state on its next poll/visit, so this
// hook just needs to invalidate the list (for the badge) once queued.
export const useBackfillAi = () => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => adminPartnersService.backfillAi(),
    onSuccess: (response) => {
      qc.invalidateQueries({ queryKey: destinationKeys.lists() });
      toast.success(
        t("partners.admin.destinations.ai.backfillStarted", {
          count: response.destinationIds.length,
        }),
      );
    },
    onError: (error) => {
      toast.error(
        describeError(t("partners.admin.destinations.ai.backfillError"), error),
      );
    },
  });

  return {
    backfill: () => mutation.mutate(),
    isBackfilling: mutation.isPending,
  };
};

// Task 0032 — city attractions. Contract 2026-09-29. Same self-polling
// shape as `useAiStatus`: the status response's own `state` drives
// `refetchInterval`, no separate "is this syncing" flag needed.
const ATTRACTIONS_POLL_INTERVAL_MS = 5000;

export const useDestinationAttractions = (
  destinationId: string | undefined,
) => {
  return useQuery<PartnerDestinationAttractions>({
    queryKey: destinationKeys.attractions(destinationId || ""),
    enabled: !!destinationId,
    queryFn: () =>
      adminPartnersService.getDestinationAttractions(destinationId!),
    staleTime: 15 * 1000,
    refetchInterval: (query) => {
      const state: PartnerAttractionsState | undefined =
        query.state.data?.state;
      return state === "queued" || state === "running"
        ? ATTRACTIONS_POLL_INTERVAL_MS
        : false;
    },
  });
};

export const useRefreshDestinationAttractions = (
  destinationId: string | undefined,
) => {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      if (!destinationId) throw new Error("destinationId is required");
      return adminPartnersService.refreshDestinationAttractions(destinationId);
    },
    onSuccess: () => {
      if (!destinationId) return;
      qc.invalidateQueries({
        queryKey: destinationKeys.attractions(destinationId),
      });
    },
    onError: (error) => {
      toast.error(
        describeError(
          t("partners.admin.destinations.attractions.refreshError"),
          error,
        ),
      );
    },
  });

  return {
    refresh: () => mutation.mutate(),
    isRefreshing: mutation.isPending,
  };
};
